/**
 * AGENCY LONG-SCROLL BLOCKS (Iteration 138 Phase A)
 *
 * Six new editable blocks for replicating high-end, custom-coded service
 * agency sites (think actionplumbingsr.com): glass-morphism stat tile,
 * hover-zoom service card row, emergency CTA band, why-us 6-grid, animated
 * 4-stat row, and inline review badge.
 *
 * Block types (all registered in EXT_BLOCK_TYPES so the universal content
 * editor handles editing):
 *   - agency_glass_stat       { eyebrow, title, tagline, stats[] }
 *   - agency_service_cards    { title, tagline, items[] }
 *   - agency_cta_band         { eyebrow, title, tagline, phone, buttons[] }
 *   - agency_why_grid         { title, tagline, items[] }
 *   - agency_stat_quad        { title, stats[] }
 *   - agency_review_badge     { platform, rating, count, link }
 */
import React from 'react';
import { SafeImg } from '../../SafeImg';
import { Btn, H1, Eyebrow, tidFor, E } from './_shared';
import { Star, Phone, ShieldCheck, Clock, Sparkles } from 'lucide-react';

const ACCENT_FALLBACK = '#F97316'; // bold orange like the reference site
const INK_FALLBACK    = '#0E0E23';
const DEEP_BG         = '#0B0F1A';

// ─────────────────────────────────────────────────────────────────────────
// 1. GLASS STAT BOX — frosted glass card floating on a dark gradient hero
// ─────────────────────────────────────────────────────────────────────────
export function AgencyGlassStat({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const accent = c.accent || theme.accent || ACCENT_FALLBACK;
  const stats = c.stats || [
    { value: '20+',  label: 'Years Serving' },
    { value: '5,000+', label: 'Happy Clients' },
    { value: '24/7', label: 'Emergency Calls' },
  ];
  const bgImg = c.image_url || 'https://images.unsplash.com/photo-1581094288338-2314dddb7ece?w=1600&q=80';
  return (
    <section
      data-block-type={block.type}
      data-layout="glass-stat-box"
      className="relative px-6 py-24 md:py-32 overflow-hidden"
      style={{ background: DEEP_BG, color: '#fff', fontFamily: theme.bodyFont || 'inherit' }}
    >
      <SafeImg src={bgImg} alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${DEEP_BG}E6 0%, ${DEEP_BG}99 100%)` }} />
      <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        <div>
          {c.eyebrow && (
            <p className="text-xs uppercase tracking-[0.32em] font-bold mb-4" style={{ color: accent }}>{c.eyebrow}</p>
          )}
          <h1 className="text-4xl md:text-6xl font-bold leading-[1.05] tracking-tight" style={{ fontFamily: theme.headingFont }}>
            {c.title || 'Trusted by the city since 2003'}
          </h1>
          {c.tagline && (
            <p className="mt-6 text-lg opacity-80 max-w-xl">{c.tagline}</p>
          )}
          {(c.buttons || []).length > 0 && (
            <div className="mt-10 flex flex-wrap gap-3">
              {(c.buttons || []).map((b, i) => (
                <Btn block={block} onEdit={onEdit} key={i} text={b.text} link={b.link} primary={i === 0} buttonIndex={i} color={i === 0 ? accent : null} theme={{ ...theme, accent, ink: '#fff' }} />
              ))}
            </div>
          )}
        </div>
        {/* Glass card */}
        <div
          className="relative rounded-3xl p-8 md:p-10 backdrop-blur-2xl border border-white/15 shadow-2xl"
          style={{
            background: 'linear-gradient(145deg, rgba(255,255,255,0.10) 0%, rgba(255,255,255,0.04) 100%)',
            boxShadow: `0 25px 60px -10px ${accent}30, inset 0 1px 0 rgba(255,255,255,0.15)`,
          }}
        >
          <div className="grid grid-cols-1 gap-6">
            {stats.slice(0, 4).map((s, i) => (
              <div key={i} className="flex items-end justify-between border-b border-white/10 pb-5 last:border-0 last:pb-0">
                <div>
                  <E block={block} onEdit={onEdit} field={`stats.${i}.value`} value={s.value || '—'} tag='p' className="text-4xl md:text-5xl font-bold leading-none" style={{ color: accent, fontFamily: theme.headingFont }} placeholder='42' />
                  <E block={block} onEdit={onEdit} field={`stats.${i}.label`} value={s.label || 'Metric'} tag='p' className="mt-2 text-xs uppercase tracking-[0.2em] opacity-70" placeholder='Metric' />
                </div>
                <div className="text-white/30">
                  {i === 0 ? <ShieldCheck size={28} /> : i === 1 ? <Sparkles size={28} /> : <Clock size={28} />}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 2. SERVICE CARD ROW — hover-zoom service cards with image + glow CTA
// ─────────────────────────────────────────────────────────────────────────
export function AgencyServiceCards({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const accent = c.accent || theme.accent || ACCENT_FALLBACK;
  const items = c.items || [
    { title: 'Emergency Repair',   description: 'Same-day service, 24/7 hotline.', image: 'https://images.unsplash.com/photo-1607472586893-edb57bdc0e39?w=900&q=80' },
    { title: 'Installation',       description: 'New systems with manufacturer warranty.', image: 'https://images.unsplash.com/photo-1581092918056-0c4c3acd3789?w=900&q=80' },
    { title: 'Inspection & Maintenance', description: 'Annual tune-ups, transparent pricing.', image: 'https://images.unsplash.com/photo-1581093588401-fbb62a02f120?w=900&q=80' },
  ];
  return (
    <section data-block-type={block.type} data-layout="service-card-row" className="px-6 py-20" style={{ background: theme.bg || '#F8FAFC', color: theme.ink || INK_FALLBACK }}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div className="max-w-xl">
            <E block={block} onEdit={onEdit} field='eyebrow' value={c.eyebrow || ''} tag='p' className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: accent }} placeholder='Eyebrow (optional)' />
            <E block={block} onEdit={onEdit} field='title' value={c.title || 'Services that solve the problem fast'} tag='h2' className="text-3xl md:text-5xl font-bold leading-tight tracking-tight" style={{ fontFamily: theme.headingFont }} placeholder='Headline' />
            <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 opacity-75 text-lg" placeholder='Tagline (optional)' />
          </div>
          {c.link_text && (
            <a href={c.link || '#'} className="text-sm uppercase tracking-[0.2em] font-bold border-b-2 pb-1 transition-all hover:opacity-70" style={{ borderColor: accent, color: accent }}>
              {c.link_text}
            </a>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {items.slice(0, 6).map((it, i) => (
            <div key={i} className="group relative rounded-2xl overflow-hidden bg-white shadow-sm transition-all duration-500 hover:shadow-2xl hover:-translate-y-1" style={{ borderRadius: theme.radius || 16 }}>
              <div className="aspect-[4/3] overflow-hidden bg-gray-200">
                {it.image
                  ? <SafeImg src={it.image} alt={it.title} className="w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-110" />
                  : <div className="w-full h-full bg-gradient-to-br from-gray-300 to-gray-500" />}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: `linear-gradient(180deg, transparent 40%, ${accent}40 100%)` }} />
              </div>
              <div className="p-6">
                <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='h3' className="text-xl font-bold leading-snug" style={{ fontFamily: theme.headingFont, color: theme.ink || INK_FALLBACK }} placeholder='Service title' />
                <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-2 text-sm opacity-70" placeholder='Description' />
                <div className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-bold transition-all group-hover:gap-3" style={{ color: accent }}>
                  {it.link_text || 'Learn more'}
                  <span aria-hidden>→</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 3. EMERGENCY CTA BAND — full-bleed bold strip with phone + glow button
// ─────────────────────────────────────────────────────────────────────────
export function AgencyCtaBand({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const accent = c.accent || theme.accent || ACCENT_FALLBACK;
  const phone = c.phone || '(555) 200-7777';
  return (
    <section data-block-type={block.type} data-layout="emergency-cta-band" className="px-6 py-14 md:py-20 relative overflow-hidden" style={{ background: accent, color: '#fff' }}>
      {/* soft glow halos */}
      <div className="absolute -top-32 -left-20 w-96 h-96 rounded-full blur-3xl opacity-25" style={{ background: '#fff' }} />
      <div className="absolute -bottom-32 -right-20 w-96 h-96 rounded-full blur-3xl opacity-20" style={{ background: '#000' }} />
      <div className="relative max-w-6xl mx-auto flex flex-col md:flex-row items-center md:items-end justify-between gap-8">
        <div className="text-center md:text-left">
          <E block={block} onEdit={onEdit} field='eyebrow' value={c.eyebrow || ''} tag='p' className="text-xs uppercase tracking-[0.32em] font-bold opacity-90 mb-3" placeholder='Eyebrow (optional)' />
          <E block={block} onEdit={onEdit} field='title' value={c.title || '24-hour emergency? We answer.'} tag='h2' className="text-3xl md:text-5xl font-bold leading-[1.05] tracking-tight" style={{ fontFamily: theme.headingFont }} placeholder='Headline' />
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-3 text-lg opacity-90 max-w-2xl" placeholder='Tagline (optional)' />
        </div>
        <div className="flex flex-col items-center md:items-end gap-3">
          <a
            href={`tel:${phone.replace(/[^\d+]/g, '')}`}
            className="inline-flex items-center gap-3 px-7 py-4 rounded-full text-base font-bold uppercase tracking-[0.15em] bg-white transition-all duration-300 hover:-translate-y-0.5"
            style={{
              color: accent,
              boxShadow: '0 0 0 0 rgba(255,255,255,0.6), 0 10px 30px -6px rgba(0,0,0,0.4)',
              animation: 'wbGlowPulse 2.2s ease-in-out infinite',
            }}
          >
            <Phone size={18} /> {phone}
          </a>
          <E block={block} onEdit={onEdit} field='note' value={c.note || ''} tag='p' className="text-xs uppercase tracking-[0.18em] opacity-80" placeholder='Note (optional)' />
        </div>
      </div>
      {/* glow keyframes (scoped per block — duplicated if used multiple times, that's fine) */}
      <style dangerouslySetInnerHTML={{ __html: `
        @keyframes wbGlowPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.55), 0 10px 30px -6px rgba(0,0,0,0.4); }
          50%      { box-shadow: 0 0 0 18px rgba(255,255,255,0),  0 10px 30px -6px rgba(0,0,0,0.4); }
        }
      `}} />
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 4. WHY-US GRID — 6 reasons box grid with icon + hover lift
// ─────────────────────────────────────────────────────────────────────────
export function AgencyWhyGrid({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const accent = c.accent || theme.accent || ACCENT_FALLBACK;
  const items = c.items || [
    { icon: '⚡', title: 'Fast Response',     description: 'On-site within 60 minutes inside city limits.' },
    { icon: '🛠️', title: 'Licensed Pros',    description: 'Every tech is bonded, insured & background-checked.' },
    { icon: '💰', title: 'Upfront Pricing',  description: 'No surprises — flat rates quoted before we start.' },
    { icon: '⭐', title: '5-Star Reviewed',  description: 'Hundreds of verified Google reviews back our work.' },
    { icon: '🔧', title: 'Lifetime Warranty', description: 'On parts & labor for all major installations.' },
    { icon: '🌱', title: 'Eco-Friendly',     description: 'Modern, efficient systems that lower your bills.' },
  ];
  return (
    <section data-block-type={block.type} data-layout="why-us-grid" className="px-6 py-20" style={{ background: theme.bg || '#FFFFFF', color: theme.ink || INK_FALLBACK }}>
      <div className="max-w-6xl mx-auto">
        <div className="max-w-2xl mx-auto text-center mb-14">
          <E block={block} onEdit={onEdit} field='eyebrow' value={c.eyebrow || ''} tag='p' className="text-xs uppercase tracking-[0.3em] font-bold mb-3" style={{ color: accent }} placeholder='Eyebrow (optional)' />
          <E block={block} onEdit={onEdit} field='title' value={c.title || 'Why homeowners choose us'} tag='h2' className="text-3xl md:text-5xl font-bold leading-tight tracking-tight" style={{ fontFamily: theme.headingFont }} placeholder='Headline' />
          <E block={block} onEdit={onEdit} field='tagline' value={c.tagline || ''} tag='p' className="mt-4 opacity-75 text-lg" placeholder='Tagline (optional)' />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.slice(0, 6).map((it, i) => (
            <div
              key={i}
              className="group relative p-7 rounded-2xl border bg-white/60 backdrop-blur-sm transition-all duration-500 hover:-translate-y-1 hover:shadow-xl"
              style={{ borderColor: 'rgba(15,23,42,0.08)', borderRadius: theme.radius || 16 }}
            >
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl mb-5 transition-all group-hover:scale-110"
                style={{ background: `${accent}15`, color: accent }}
              >
                {it.icon || '✦'}
              </div>
              <E block={block} onEdit={onEdit} field={`items.${i}.title`} value={it.title} tag='h3' className="text-lg font-bold" style={{ fontFamily: theme.headingFont }} placeholder='Reason title' />
              <E block={block} onEdit={onEdit} field={`items.${i}.description`} value={it.description || ''} tag='p' className="mt-2 text-sm opacity-75 leading-relaxed" placeholder='Description' />
              <div className="absolute bottom-0 left-7 right-7 h-0.5 transition-all duration-500 origin-left scale-x-0 group-hover:scale-x-100" style={{ background: accent }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 5. STAT QUAD ROW — 4 huge animated stat numbers across dark band
// ─────────────────────────────────────────────────────────────────────────
export function AgencyStatQuad({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const accent = c.accent || theme.accent || ACCENT_FALLBACK;
  const stats = c.stats || [
    { value: '20+',     label: 'Years in business' },
    { value: '15K+',    label: 'Jobs completed' },
    { value: '4.9★',    label: 'Average rating' },
    { value: '60 min',  label: 'Avg response time' },
  ];
  return (
    <section
      data-block-type={block.type}
      data-layout="stat-quad-row"
      className="px-6 py-16 md:py-24 relative overflow-hidden"
      style={{ background: c.bg_dark === false ? (theme.bg || '#F8FAFC') : DEEP_BG, color: c.bg_dark === false ? (theme.ink || INK_FALLBACK) : '#fff' }}
    >
      <div className="max-w-6xl mx-auto">
        <E block={block} onEdit={onEdit} field='title' value={c.title || ''} tag='h2' className="text-center text-3xl md:text-4xl font-bold mb-12" style={{ fontFamily: theme.headingFont }} placeholder='Headline (optional)' />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-4">
          {stats.slice(0, 4).map((s, i) => (
            <div key={i} className="text-center group">
              <E block={block} onEdit={onEdit} field={`stats.${i}.value`} value={s.value || '—'} tag='p' className="text-5xl md:text-7xl font-bold leading-none transition-all duration-500 group-hover:scale-110" style={{ color: accent, fontFamily: theme.headingFont }} placeholder='42' />
              <E block={block} onEdit={onEdit} field={`stats.${i}.label`} value={s.label || 'Metric'} tag='p' className="mt-3 text-xs md:text-sm uppercase tracking-[0.22em] opacity-80" placeholder='Metric' />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// 6. INLINE REVIEW BADGE — small Google/Yelp style chip (centered band)
// ─────────────────────────────────────────────────────────────────────────
export function AgencyReviewBadge({ block, theme, E: EProps = {} }) {
  const { onEdit } = EProps;
  const c = block.content || {};
  const tid = tidFor(block);
  const accent = c.accent || theme.accent || ACCENT_FALLBACK;
  const platform = c.platform || 'Google';
  const rating = parseFloat(c.rating || '4.9');
  const count = c.count || '432';
  const fullStars = Math.floor(rating);
  return (
    <section data-block-type={block.type} data-layout="inline-review-badge" className="px-6 py-10" style={{ background: theme.bg || '#FFFFFF', color: theme.ink || INK_FALLBACK }}>
      <a
        href={c.link || '#'}
        className="mx-auto inline-flex items-center gap-4 px-6 py-3.5 rounded-full border bg-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
        style={{ borderColor: 'rgba(15,23,42,0.10)', display: 'flex', maxWidth: 'max-content' }}
      >
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold"
            style={{ background: platform === 'Google' ? '#fff' : accent, color: platform === 'Google' ? '#4285F4' : '#fff', boxShadow: 'inset 0 0 0 1px rgba(0,0,0,0.06)' }}
          >
            {platform === 'Google' ? 'G' : platform[0]}
          </div>
          <div className="text-left">
            <p className="text-[10px] uppercase tracking-[0.22em] opacity-60">Reviewed on {platform}</p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="font-bold text-sm">{rating.toFixed(1)}</span>
              <span className="flex" style={{ color: '#FBBF24' }}>
                {[0, 1, 2, 3, 4].map(i => (
                  <Star key={i} size={13} fill={i < fullStars ? '#FBBF24' : 'transparent'} strokeWidth={1.5} />
                ))}
              </span>
              <span className="text-xs opacity-60">· {count} reviews</span>
            </div>
          </div>
        </div>
        <span className="text-xs uppercase tracking-[0.2em] font-bold pl-3 border-l border-current/10" style={{ color: accent }}>
          {c.cta || 'Read all'}
          <span aria-hidden className="ml-1.5">→</span>
        </span>
      </a>
    </section>
  );
}

// Combined dispatcher — main renderer extension calls this single function.
export function AgencyExt({ block, theme, E: EProps = {} }) {
  switch (block.type) {
    case 'agency_glass_stat':     return <AgencyGlassStat    block={block} theme={theme} E={EProps} />;
    case 'agency_service_cards':  return <AgencyServiceCards block={block} theme={theme} E={EProps} />;
    case 'agency_cta_band':       return <AgencyCtaBand      block={block} theme={theme} E={EProps} />;
    case 'agency_why_grid':       return <AgencyWhyGrid      block={block} theme={theme} E={EProps} />;
    case 'agency_stat_quad':      return <AgencyStatQuad     block={block} theme={theme} E={EProps} />;
    case 'agency_review_badge':   return <AgencyReviewBadge  block={block} theme={theme} E={EProps} />;
    default: return null;
  }
}

export const AGENCY_BLOCK_TYPES = new Set([
  'agency_glass_stat',
  'agency_service_cards',
  'agency_cta_band',
  'agency_why_grid',
  'agency_stat_quad',
  'agency_review_badge',
]);
